import { useCallback, useEffect, useRef } from "react";
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createSeriesMarkers,
  LineStyle,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import type { Candle, IndicatorsResponse } from "@/lib/types";

export interface CostLine {
  price: number;
  label: string;
  color: string;
}

export interface ChartFeatures {
  volume: boolean;
  ema: boolean;
  rsi: boolean;
  macd: boolean;
  signals: boolean;
  costLines: boolean;
}

interface Props {
  candles: Candle[];
  indicators: IndicatorsResponse | null;
  costLines: CostLine[];
  features: ChartFeatures;
}

const UP = "#22c55e";
const DOWN = "#ef4444";

interface SeriesSet {
  candle: ISeriesApi<"Candlestick">;
  volume?: ISeriesApi<"Histogram">;
  ema9?: ISeriesApi<"Line">;
  ema21?: ISeriesApi<"Line">;
  rsi?: ISeriesApi<"Line">;
  macdHist?: ISeriesApi<"Histogram">;
  macdLine?: ISeriesApi<"Line">;
  macdSignal?: ISeriesApi<"Line">;
}

function dedupeSorted(candles: Candle[]): Candle[] {
  const sorted = [...candles].sort((a, b) => a.t - b.t);
  const out: Candle[] = [];
  let last = -1;
  for (const c of sorted) {
    if (c.t === last) out[out.length - 1] = c;
    else out.push(c);
    last = c.t;
  }
  return out;
}

// lightweight-charts renders UTCTimestamps as UTC. Our data is UTC, but we want
// the axis/crosshair in Stockholm wall-clock time, so we shift each timestamp by
// the Stockholm UTC offset at that moment (DST-correct, computed per bar).
function stockholmOffsetSec(utcSec: number): number {
  const date = new Date(utcSec * 1000);
  const utc = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
  const local = new Date(date.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));
  return Math.round((local.getTime() - utc.getTime()) / 1000);
}
const tof = (t: number) => (t + stockholmOffsetSec(t)) as UTCTimestamp;
const lineData = (pts: { t: number; value: number | null }[]) =>
  pts.filter((p) => p.value != null).map((p) => ({ time: tof(p.t), value: p.value as number }));

export function PriceChart({ candles, indicators, costLines, features }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<SeriesSet | null>(null);
  const priceLinesRef = useRef<ReturnType<ISeriesApi<"Candlestick">["createPriceLine"]>[]>([]);
  // Latest data kept in refs so both the build and data effects can populate.
  const dataRef = useRef({ candles, indicators });
  dataRef.current = { candles, indicators };

  const populate = useCallback(() => {
    const s = seriesRef.current;
    const chart = chartRef.current;
    if (!s || !chart) return;
    const { candles, indicators } = dataRef.current;
    if (candles.length === 0) return;

    const data = dedupeSorted(candles);
    s.candle.setData(data.map((c) => ({ time: tof(c.t), open: c.o, high: c.h, low: c.l, close: c.c })));
    s.volume?.setData(
      data.map((c) => ({ time: tof(c.t), value: c.v, color: c.c >= c.o ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)" })),
    );

    if (indicators) {
      s.ema9?.setData(lineData(indicators.ema9));
      s.ema21?.setData(lineData(indicators.ema21));
      s.rsi?.setData(lineData(indicators.rsi));
      s.macdLine?.setData(indicators.macd.filter((m) => m.macd != null).map((m) => ({ time: tof(m.t), value: m.macd as number })));
      s.macdSignal?.setData(indicators.macd.filter((m) => m.signal != null).map((m) => ({ time: tof(m.t), value: m.signal as number })));
      s.macdHist?.setData(
        indicators.macd
          .filter((m) => m.histogram != null)
          .map((m) => ({ time: tof(m.t), value: m.histogram as number, color: (m.histogram as number) >= 0 ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)" })),
      );

      const markers: SeriesMarker<Time>[] = features.signals
        ? indicators.signals.map((sig) => ({
            time: tof(sig.t),
            position: sig.type === "buy" ? "belowBar" : "aboveBar",
            color: sig.type === "buy" ? UP : DOWN,
            shape: sig.type === "buy" ? "arrowUp" : "arrowDown",
            text: sig.type === "buy" ? "Köp" : "Sälj",
            size: sig.strength >= 2 ? 2 : 1,
          }))
        : [];
      createSeriesMarkers(s.candle, markers);
    }
    chart.timeScale().fitContent();
  }, [features.signals]);

  // (Re)build the chart whenever the feature set changes — only enabled
  // series/panes are created, so toggled-off panes don't leave empty space.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: "transparent" },
        textColor: "#a1a1aa",
        fontFamily: "inherit",
        panes: { separatorColor: "#27272a", separatorHoverColor: "#3f3f46" },
      },
      grid: {
        vertLines: { color: "rgba(63,63,70,0.25)" },
        horzLines: { color: "rgba(63,63,70,0.25)" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "#27272a" },
      timeScale: { borderColor: "#27272a", timeVisible: true, secondsVisible: false },
      localization: { locale: "sv-SE" },
      // Static view: the period/interval buttons control the range, not scroll/zoom.
      handleScroll: false,
      handleScale: false,
    });
    chartRef.current = chart;

    const candle = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      borderUpColor: UP,
      borderDownColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
      priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
    });
    const set: SeriesSet = { candle };

    if (features.volume) {
      set.volume = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "vol" });
      chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    }
    if (features.ema) {
      set.ema9 = chart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      set.ema21 = chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    }

    // Indicator panes get sequential indices only for the ones that are enabled.
    let pane = 1;
    if (features.rsi) {
      const rsi = chart.addSeries(LineSeries, { color: "#a855f7", lineWidth: 2, priceLineVisible: false }, pane);
      rsi.createPriceLine({ price: 70, color: "#ef4444", lineStyle: LineStyle.Dashed, lineWidth: 1, title: "70" });
      rsi.createPriceLine({ price: 30, color: "#22c55e", lineStyle: LineStyle.Dashed, lineWidth: 1, title: "30" });
      set.rsi = rsi;
      pane++;
    }
    if (features.macd) {
      set.macdHist = chart.addSeries(HistogramSeries, { priceLineVisible: false }, pane);
      set.macdLine = chart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, pane);
      set.macdSignal = chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, pane);
    }

    seriesRef.current = set;
    priceLinesRef.current = [];
    populate();

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.volume, features.ema, features.rsi, features.macd, populate]);

  // Repopulate when data changes (no rebuild → keeps it smooth on polls).
  useEffect(() => {
    populate();
  }, [candles, indicators, populate]);

  // Cost-basis price lines (toggleable, on the candle series).
  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    priceLinesRef.current.forEach((pl) => s.candle.removePriceLine(pl));
    priceLinesRef.current = [];
    if (!features.costLines) return;
    for (const line of costLines) {
      priceLinesRef.current.push(
        s.candle.createPriceLine({
          price: line.price,
          color: line.color,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: line.label,
        }),
      );
    }
  }, [costLines, features.costLines, features.volume, features.ema, features.rsi, features.macd, candles]);

  return <div ref={containerRef} className="h-[460px] w-full" />;
}
